/**
 * Chat stream state derivation — pure logic, no React.
 *
 * PR-AAU-C — wires the SSE event stream from helper-chat / ecosystem-query
 * into UI state for:
 *   - ArgusEye animation state (`idle | thinking | speaking | alert | sleeping | listening`)
 *   - Confidence chip color + tier label + tooltip rationale
 *   - Top status badges (Web Search ON, Library + AI, connectors, agents)
 *
 * Centralizing the logic here keeps `chat-messages.tsx` and the route
 * file thin and makes every derivation directly unit-testable without
 * a DOM/jsdom dependency (matches the existing test style of this
 * package — see `slash-commands.test.ts`).
 */

import type { ArgusState } from '@/components/icons/ArgusEye';

// ── Stream phase ───────────────────────────────────────────────────────────

/**
 * Phase of an in-flight assistant turn. Derived from which SSE events
 * have arrived so far. The mapping to `ArgusState` lives in
 * `streamPhaseToArgusState` below.
 *
 *   - `idle`         no in-flight turn / nothing requested yet
 *   - `awaiting`     request sent, no SSE events yet OR only metadata
 *                    (the "thinking" phase — model is reasoning, no
 *                    tokens yet)
 *   - `tool_calling` model has emitted a `tool-call` / `pipeline:routed`
 *                    event — still working, still no user-visible text
 *   - `streaming`    `token` events are arriving — model is "speaking"
 *   - `error`        terminal: a `type: 'error'` SSE event arrived OR
 *                    the fetch itself failed
 *   - `done`         terminal: `type: 'done'` SSE event arrived
 */
export type StreamPhase =
  | 'idle'
  | 'awaiting'
  | 'thinking'
  | 'tool_calling'
  | 'streaming'
  | 'error'
  | 'done';

/**
 * Map an in-flight stream phase to an `ArgusEye` animation state.
 *
 * Visual mapping (founder direction in project_ask_argus_page_design):
 *   - awaiting / tool_calling → `thinking` (blue, rays rotating)
 *   - streaming               → `speaking` (purple, pupil bouncing)
 *   - error                   → `alert`    (amber, scanline + shake)
 *   - done                    → `idle`     (gentle drift)
 *   - idle                    → `idle`
 *
 * Note: `listening` and `sleeping` aren't currently driven by the
 * stream — they're reserved for future composer-active and long-idle
 * states.
 */
export function streamPhaseToArgusState(phase: StreamPhase): ArgusState {
  switch (phase) {
    case 'awaiting':
    case 'thinking':
    case 'tool_calling':
      return 'thinking';
    case 'streaming':
      return 'speaking';
    case 'error':
      return 'alert';
    case 'done':
    case 'idle':
    default:
      return 'idle';
  }
}

// ── SSE event type ─────────────────────────────────────────────────────────

/**
 * Discriminated event union covering BOTH SSE shapes the Ask Argus
 * surface consumes today:
 *
 *   1. `/ecosystem/query` — events look like `{ type, data: {...} }`
 *      (each citation is its own `{ type: 'citation', data }` event).
 *
 *   2. `/studio/helper/chat` (PR-AAU-A) — events look like
 *      `{ type, ...payload }` with no `data` wrapping, AND emits
 *      `{ type: 'citations', citations: [...] }` (plural, batched)
 *      plus `{ type: 'confidence', confidence, rationale }`.
 *
 * The reducer normalizes both shapes into a single stream state.
 */
export type StreamEvent =
  | { type: 'metadata'; data?: any; toggles?: ChatToggles; [k: string]: any }
  | { type: 'pipeline:phase' | 'pipeline-phase'; data?: any; phase?: string; label?: string; stage?: string; status?: string; [k: string]: any }
  | { type: 'token'; data?: { content: string }; content?: string }
  | { type: 'tool-call' | 'tool_call'; data?: any; [k: string]: any }
  | { type: 'pipeline:routed' | 'pipeline-routed'; data?: any; [k: string]: any }
  | { type: 'citation'; data?: any; [k: string]: any }
  | { type: 'citations'; data?: { citations: any[] }; citations?: any[] }
  | { type: 'confidence'; data?: { score?: number; confidence?: number; rationale?: string }; confidence?: number; rationale?: string }
  | { type: 'error'; data?: { code?: string; message?: string }; code?: string; message?: string }
  | { type: 'heartbeat'; data?: any }
  | { type: 'done'; data?: any; [k: string]: any };

// ── Toggles surfaced via `metadata.toggles` ────────────────────────────────

export interface ChatToggles {
  thinkingMode?: 'quick' | 'standard' | 'deep';
  webSearch?: boolean;
  knowledgeMode?: 'library_only' | 'library_plus_ai';
}

// ── Confidence chip derivation ─────────────────────────────────────────────

export type ConfidenceTier = 'high' | 'medium' | 'low' | 'none';

export interface ConfidenceChipState {
  tier: ConfidenceTier;
  /** Emoji marker matching the founder spec. */
  marker: '🟢' | '🟡' | '🔴' | '⏸';
  /** Short label like "High", "Medium", "Low", "No data". */
  label: string;
  /** Tooltip body — defaults to a brief explanation per tier. */
  tooltip: string;
}

/**
 * Derive a confidence chip from the SSE `confidence` event payload.
 *
 * From the founder spec:
 *   ≥0.85  → High      🟢
 *   0.6-0.85 → Medium  🟡
 *   <0.6   → Low       🔴
 *   rationale === 'placeholder' → No data ⏸
 *
 * `confidence === undefined` (no event ever received) also returns
 * `'none'` so the chip simply doesn't render.
 */
export function deriveConfidenceChip(
  confidence: number | undefined,
  rationale: string | undefined,
): ConfidenceChipState {
  // PR-AAU-A backend emits `rationale: 'placeholder'` while the RAG
  // pipeline is still being wired. Treat that as "no data" rather
  // than misleading "Low confidence".
  if (rationale === 'placeholder' || confidence === undefined || confidence === null) {
    return {
      tier: 'none',
      marker: '⏸',
      label: 'No data',
      tooltip:
        rationale && rationale !== 'placeholder'
          ? rationale
          : 'Confidence scoring is not yet wired for this response.',
    };
  }

  // Scale-normalize. The chip's tiers are on a 0..1 scale, but the backend
  // confidence scorer (and persisted message.confidenceScore) is 0-100, and the
  // studio-helper `confidence` event is 0..1 — both feed this function. Without
  // this, a 0-100 score (e.g. 60 = honest "Medium") read as `60 >= 0.85` always
  // rendered "High" — the root of "High confidence on unverified data" in the
  // chip. Accept either scale: anything > 1 is treated as a 0-100 percentage.
  if (confidence > 1) confidence = confidence / 100;

  if (confidence >= 0.85) {
    return {
      tier: 'high',
      marker: '🟢',
      label: 'High',
      tooltip: rationale ?? 'High confidence — answer well-grounded in retrieved sources.',
    };
  }
  if (confidence >= 0.6) {
    return {
      tier: 'medium',
      marker: '🟡',
      label: 'Medium',
      tooltip: rationale ?? 'Moderate confidence — some claims are well-grounded; verify before relying on details.',
    };
  }
  return {
    tier: 'low',
    marker: '🔴',
    label: 'Low',
    tooltip: rationale ?? 'Low confidence — retrieval was weak; treat the answer as exploratory.',
  };
}

// ── Top status badges ──────────────────────────────────────────────────────

export interface ChatStatusBadge {
  id: string;
  /** Short label rendered on the chip. */
  label: string;
  /**
   * Tailwind color classes for the chip. The component layer concatenates
   * these with the breathing-pulse animation utility.
   */
  className: string;
  /** Whether this badge should breathe (slow pulse) vs render solid. */
  pulse: boolean;
}

/**
 * Compute the top status badges from the active toggle state. Returns
 * only the badges that should be visible — the caller renders the
 * resulting array in order.
 *
 * Founder spec:
 *   🟣 Web Search ON       — purple, breathing
 *   🔵 Library + AI        — blue, solid
 *   🟢 [N] connectors active — green, solid (N≥1)
 *   🟡 [M] agents in chat   — amber, solid (M≥1)
 */
export function deriveStatusBadges(input: {
  toggles?: ChatToggles;
  connectorCount?: number;
  agentCount?: number;
}): ChatStatusBadge[] {
  const badges: ChatStatusBadge[] = [];
  const t = input.toggles ?? {};

  if (t.webSearch === true) {
    badges.push({
      id: 'web-search',
      label: '🟣 Web Search ON',
      className: 'bg-purple-500/10 text-purple-600 dark:text-purple-300 border-purple-500/30',
      pulse: true,
    });
  }
  if (t.knowledgeMode === 'library_plus_ai') {
    badges.push({
      id: 'library-plus-ai',
      label: '🔵 Library + AI',
      className: 'bg-blue-500/10 text-blue-600 dark:text-blue-300 border-blue-500/30',
      pulse: false,
    });
  }
  const cc = input.connectorCount ?? 0;
  if (cc > 0) {
    badges.push({
      id: 'connectors',
      label: `🟢 ${cc} connector${cc === 1 ? '' : 's'} active`,
      className: 'bg-green-500/10 text-green-600 dark:text-green-300 border-green-500/30',
      pulse: false,
    });
  }
  const ac = input.agentCount ?? 0;
  if (ac > 0) {
    badges.push({
      id: 'agents',
      label: `🟡 ${ac} agent${ac === 1 ? '' : 's'} in chat`,
      className: 'bg-amber-500/10 text-amber-600 dark:text-amber-300 border-amber-500/30',
      pulse: false,
    });
  }
  return badges;
}

// ── SSE event reducer helpers ──────────────────────────────────────────────

/**
 * Compute the next stream phase given the current phase + an incoming
 * SSE event. Pure function — used by the route file's SSE consumer
 * and exercised directly by unit tests.
 *
 * Idempotent against unknown event types (returns previous phase).
 */
export function reduceStreamPhase(
  prev: StreamPhase,
  evt: { type: string },
): StreamPhase {
  // Once we've moved to a terminal state, only an explicit reset
  // (caller's responsibility) can move us away from it.
  if (prev === 'error' || prev === 'done') return prev;

  switch (evt.type) {
    case 'metadata':
      // Stay in `awaiting` — model hasn't produced anything yet.
      return prev === 'idle' ? 'awaiting' : prev;
    case 'pipeline:phase':
    case 'pipeline-phase':
      return prev === 'idle' || prev === 'awaiting' ? 'thinking' : prev;
    case 'tool-call':
    case 'tool_call':
    case 'pipeline:routed':
    case 'pipeline-routed':
      // Tool calling is still "thinking" visually but we track it
      // separately so future UI (e.g., "Using tool X…" status line)
      // can surface it.
      return 'tool_calling';
    case 'token':
      return 'streaming';
    case 'error':
      return 'error';
    case 'done':
      return 'done';
    default:
      return prev;
  }
}

/**
 * Extract toggles from a `metadata` SSE event. Returns undefined if the
 * event doesn't carry toggles (older backend builds OR a non-metadata
 * event).
 *
 * Supports both helper-chat (`evt.toggles`) and ecosystem-query
 * (`evt.data.toggles`) shapes.
 */
export function extractToggles(evt: any): ChatToggles | undefined {
  if (!evt || typeof evt !== 'object') return undefined;
  const fromTop = evt.toggles;
  const fromData = evt.data && typeof evt.data === 'object' ? evt.data.toggles : undefined;
  const t = fromTop ?? fromData;
  if (!t || typeof t !== 'object') return undefined;
  const out: ChatToggles = {};
  if (t.thinkingMode === 'quick' || t.thinkingMode === 'standard' || t.thinkingMode === 'deep') {
    out.thinkingMode = t.thinkingMode;
  }
  if (typeof t.webSearch === 'boolean') out.webSearch = t.webSearch;
  if (t.knowledgeMode === 'library_only' || t.knowledgeMode === 'library_plus_ai') {
    out.knowledgeMode = t.knowledgeMode;
  }
  return out;
}

/**
 * Extract a (confidence, rationale) pair from a `confidence` SSE event.
 *
 * Supports both PR-AAU-A helper-chat shape ({ type, confidence, rationale })
 * and the older ecosystem-query shape ({ type, data: { score } }).
 */
export function extractConfidence(evt: any): { confidence?: number; rationale?: string } {
  if (!evt || typeof evt !== 'object') return {};
  // PR-AAU-A: flat
  if (typeof evt.confidence === 'number') {
    return {
      confidence: evt.confidence,
      rationale: typeof evt.rationale === 'string' ? evt.rationale : undefined,
    };
  }
  // ecosystem-query: nested `data`
  if (evt.data && typeof evt.data === 'object') {
    if (typeof evt.data.confidence === 'number') {
      return {
        confidence: evt.data.confidence,
        rationale: typeof evt.data.rationale === 'string' ? evt.data.rationale : undefined,
      };
    }
    if (typeof evt.data.score === 'number') {
      return { confidence: evt.data.score };
    }
  }
  return {};
}

/**
 * Extract a citations array from a `citations` plural event (PR-AAU-A
 * helper-chat shape) OR a single `citation` event (ecosystem-query
 * shape). Returns undefined for unrelated events.
 */
export function extractCitations(evt: any): any[] | undefined {
  if (!evt || typeof evt !== 'object') return undefined;
  if (evt.type === 'citations') {
    if (Array.isArray(evt.citations)) return evt.citations;
    if (evt.data && Array.isArray(evt.data.citations)) return evt.data.citations;
    return [];
  }
  if (evt.type === 'citation') {
    const single = evt.data ?? evt;
    return single && typeof single === 'object' ? [single] : [];
  }
  return undefined;
}

// ── Error model ────────────────────────────────────────────────────────────

export function extractPipelinePhaseLabel(evt: any): string | undefined {
  if (!evt || typeof evt !== 'object') return undefined;
  if (evt.type !== 'pipeline:phase' && evt.type !== 'pipeline-phase') return undefined;

  const direct = typeof evt.label === 'string' ? evt.label.trim() : '';
  if (direct) return direct.slice(0, 80);

  const data = evt.data && typeof evt.data === 'object' ? evt.data : undefined;
  const nested = data && typeof data.label === 'string' ? data.label.trim() : '';
  if (nested) return nested.slice(0, 80);

  return undefined;
}

export interface ChatErrorState {
  code?: string;
  message: string;
  /** Original error event payload, for "Copy error details". */
  raw?: unknown;
}

/**
 * Normalize a `type: 'error'` SSE event (or a fetch-side failure) into a
 * `ChatErrorState` for the error card component to render.
 */
export function normalizeErrorEvent(evt: any): ChatErrorState {
  if (!evt || typeof evt !== 'object') {
    return { message: 'Stream error', raw: evt };
  }
  const code =
    typeof evt.code === 'string'
      ? evt.code
      : evt.data && typeof evt.data.code === 'string'
        ? evt.data.code
        : undefined;
  const message =
    (typeof evt.message === 'string' && evt.message) ||
    (evt.data && typeof evt.data.message === 'string' && evt.data.message) ||
    code ||
    'Stream error';
  return { code, message, raw: evt };
}
